import { Download } from 'lucide-react';
import { Button, FilePicker, toast } from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import { countCsvRows } from '../targetingCsv';
import type { TargetingCsvControl } from '../TargetingForm.types';
import { InclusionToggle } from '../InclusionToggle';
import { TargetingSection } from '../TargetingSection';

export function TargetingCsvSection({
  control,
  included,
  onIncludedChange,
  title,
}: {
  control?: TargetingCsvControl | undefined;
  included?: boolean | undefined;
  onIncludedChange?: ((included: boolean) => void) | undefined;
  title: string;
}) {
  return (
    <TargetingSection
      title={title}
      action={
        included !== undefined && onIncludedChange ? (
          <InclusionToggle included={included} onIncludedChange={onIncludedChange} />
        ) : undefined
      }
    >
      <div className="border-input grid gap-4 rounded-md border px-4 py-4 lg:grid-cols-[170px_minmax(0,1fr)_auto] lg:items-end">
        <div className="self-center">
          <h4 className="text-sm font-semibold">{dashboardT('targeting.csvData')}</h4>
          <p className="text-muted-foreground mt-0.5 text-xs">
            {dashboardT('common.item', { count: control?.count ?? 0 })}
          </p>
        </div>
        <FilePicker
          label={dashboardT('targeting.csvFile', { title })}
          accept=".csv,text/csv"
          buttonLabel={dashboardT('targeting.uploadCsv')}
          value={control?.file ?? null}
          disabled={!control}
          helperText={control?.helperText ?? dashboardT('targeting.csvHelp')}
          helperTextClassName="text-xs"
          onFileChange={(file) => {
            if (!control) return;
            if (!file) {
              control.onFileChange(null, control.count);
              return;
            }
            void countCsvRows(file)
              .then((rowCount) => {
                control.onFileChange(file, rowCount);
              })
              .catch(() => {
                control.onFileChange(null, control.count);
                toast.error(dashboardT('targeting.csvReadError'));
              });
          }}
        />
        <div className="flex flex-wrap gap-2 lg:justify-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!control?.targetingId || control.exporting}
            onClick={() => {
              control?.onExport();
            }}
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            {dashboardT('targeting.exportCsv')}
          </Button>
        </div>
        {control?.notice ? <div className="lg:col-span-3">{control.notice}</div> : null}
      </div>
    </TargetingSection>
  );
}
